import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { Button, Spinner, Badge } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import CommonRichTextEditor from "../../components/Common/CommonRichTextEditor";
import { PLATFORM_NAME, LOAN_TYPES, COLLATERAL_TYPES } from "../../CommonData/Data/constant";
import { getTermsConditions, saveTermsConditions } from "../../helpers/API";
import callApi from "../../helpers/CallApi";
import { toast } from "react-toastify";

const TermsCondition = ({ t }) => {
  document.title = t("termsCondition.pageTitle", { PLATFORM_NAME });

  const [activeTab, setActiveTab] = useState("loan");
  const [activeSubTab, setActiveSubTab] = useState("");
  const [termsData, setTermsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dataVersion, setDataVersion] = useState(0);

  // Build combined items list
  const loanItems = LOAN_TYPES.map((lt) => ({
    ...lt,
    label: t(lt.labelKey),
    category: "loan",
    key: `loan_${lt.value}`,
  }));

  const collateralItems = COLLATERAL_TYPES.map((ct) => ({
    ...ct,
    label: t(ct.labelKey),
    category: "collateral",
    key: `collateral_${ct.value}`,
  }));

  const allItems = activeTab === "loan" ? loanItems : collateralItems;

  // Set default sub tab when main tab changes
  useEffect(() => {
    if (allItems.length > 0) {
      setActiveSubTab(allItems[0].key);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Decode HTML entities (e.g. &lt; → <, &gt; → >, &amp; → &)
  const decodeHtmlEntities = (str) => {
    if (!str) return "";
    const textarea = document.createElement("textarea");
    textarea.innerHTML = str;
    return textarea.value;
  };

  // Fetch terms data from API
  useEffect(() => {
    setLoading(true);
    callApi({
      callback: getTermsConditions,
      payload: [],
      onSuccess: (res) => {
        const data = {};
        if (res && Array.isArray(res)) {
          res.forEach((item) => {
            data[item.key] = decodeHtmlEntities(item.content || "");
          });
        } else if (res && typeof res === "object" && !Array.isArray(res)) {
          Object.keys(res).forEach((key) => {
            if (key !== "message" && key !== "status") {
              data[key] = decodeHtmlEntities(res[key] || "");
            }
          });
        }
        setTermsData(data);
        setDataVersion((v) => v + 1);
        setLoading(false);
      },
      onError: (error) => {
        console.error("Failed to load terms:", error);
        setLoading(false);
        // Load from localStorage as fallback
        try {
          const saved = localStorage.getItem("termsConditions");
          if (saved) {
            setTermsData(JSON.parse(saved));
            setDataVersion((v) => v + 1);
          }
        } catch (e) {
          console.error("Failed to load from localStorage:", e);
        }
      },
    });
  }, []);

  // Handle editor content change
  const handleEditorChange = useCallback((key, content) => {
    setTermsData((prev) => ({
      ...prev,
      [key]: content,
    }));
  }, []);

  // Save terms for a specific key
  const handleSave = (key) => {
    setSaving(true);
    // const payload = {
    //   key: key,
    //   content: termsData[key] || "",
    //   category: key.startsWith("loan_") ? "loan" : "collateral",
    //   typeValue: key.startsWith("loan_") ? key.replace("loan_", "") : key.replace("collateral_", ""),
    // };

    callApi({
      callback: saveTermsConditions,
      payload: [{ termsAndConditions: termsData }],
      onSuccess: ({ message }) => {
        toast.success(message || t("termsCondition.messages.saveSuccess"));
        setSaving(false);
        // Also save to localStorage as backup
        try {
          const allData = { ...termsData };
          localStorage.setItem("termsConditions", JSON.stringify(allData));
        } catch (e) {
          console.error("Failed to save to localStorage:", e);
        }
      },
      onError: (error) => {
        // Save to localStorage as fallback
        try {
          localStorage.setItem("termsConditions", JSON.stringify(termsData));
          toast.success(t("termsCondition.messages.savedLocally"));
        } catch (e) {
          toast.error(error || t("termsCondition.messages.saveError"));
        }
        setSaving(false);
      },
    });
  };

  // Save all terms at once
  const handleSaveAll = () => {
    setSaving(true);
    const allTerms = Object.keys(termsData).map((key) => ({
      key,
      content: termsData[key] || "",
      category: key.startsWith("loan_") ? "loan" : "collateral",
      typeValue: key.startsWith("loan_") ? key.replace("loan_", "") : key.replace("collateral_", ""),
    }));

    callApi({
      callback: saveTermsConditions,
      payload: [{ termsAndConditions: JSON.stringify(allTerms) }],
      onSuccess: ({ message }) => {
        toast.success(message || t("termsCondition.messages.saveAllSuccess"));
        setSaving(false);
        try {
          localStorage.setItem("termsConditions", JSON.stringify(termsData));
        } catch (e) {
          console.error("Failed to save to localStorage:", e);
        }
      },
      onError: (error) => {
        try {
          localStorage.setItem("termsConditions", JSON.stringify(termsData));
          toast.success(t("termsCondition.messages.savedLocally"));
        } catch (e) {
          toast.error(error || t("termsCondition.messages.saveError"));
        }
        setSaving(false);
      },
    });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title={t("termsCondition.breadcrumb.title")}
          breadcrumbItem={t("termsCondition.breadcrumb.item")}
        />

        <Row>
          <Col lg={12}>
            {/* Main Category Tabs */}
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 className="card-title mb-1">{t("termsCondition.heading")}</h4>
                    <p className="text-muted mb-0">{t("termsCondition.subHeading")}</p>
                  </div>
                  <Button
                    variant="success"
                    onClick={handleSaveAll}
                    disabled={saving || loading}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {t("termsCondition.buttons.saving")}
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line me-1"></i>
                        {t("termsCondition.buttons.saveAll")}
                      </>
                    )}
                  </Button>
                </div>

                <Nav tabs className="nav-tabs-custom nav-justified mb-3">
                  <NavItem>
                    <NavLink
                      className={activeTab === "loan" ? "active" : ""}
                      onClick={() => setActiveTab("loan")}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="mdi mdi-cash-multiple me-1"></i>
                      {t("termsCondition.tabs.loanTypes")}
                      <Badge bg="primary" className="ms-2">{LOAN_TYPES.length}</Badge>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "collateral" ? "active" : ""}
                      onClick={() => setActiveTab("collateral")}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="mdi mdi-shield-lock me-1"></i>
                      {t("termsCondition.tabs.collateralTypes")}
                      <Badge bg="info" className="ms-2">{COLLATERAL_TYPES.length}</Badge>
                    </NavLink>
                  </NavItem>
                </Nav>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="text-muted mt-2">{t("termsCondition.loading")}</p>
                  </div>
                ) : (
                  <Row>
                    {/* Left: Sub-type navigation */}
                    <Col md={3}>
                      <Nav pills className="flex-column">
                        {allItems.map((item) => (
                          <NavItem key={item.key}>
                            <NavLink
                              className={activeSubTab === item.key ? "active mb-2" : "mb-2"}
                              onClick={() => setActiveSubTab(item.key)}
                              style={{
                                cursor: "pointer",
                                borderRadius: "8px",
                                padding: "10px 16px",
                                backgroundColor: activeSubTab === item.key ? "#556ee6" : "#f8f9fa",
                                color: activeSubTab === item.key ? "#fff" : "#495057",
                                border: activeSubTab === item.key ? "none" : "1px solid #e9ecef",
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <span>{item.label}</span>
                                {termsData[item.key] && (
                                  <Badge
                                    bg={activeSubTab === item.key ? "light" : "success"}
                                    text={activeSubTab === item.key ? "dark" : "white"}
                                    pill
                                    style={{ fontSize: "9px" }}
                                  >
                                    <i className="ri-check-line"></i>
                                  </Badge>
                                )}
                              </div>
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>
                    </Col>

                    {/* Right: Editor */}
                    <Col md={9}>
                      <TabContent activeTab={activeSubTab}>
                        {allItems.map((item) => (
                          <TabPane tabId={item.key} key={item.key}>
                            <Card className="border shadow-none mb-0">
                              <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <div>
                                    <h5 className="mb-1">
                                      {t("termsCondition.editorTitle", { type: item.label })}
                                    </h5>
                                    <p className="text-muted small mb-0">
                                      {t("termsCondition.editorDescription", { type: item.label })}
                                    </p>
                                  </div>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleSave(item.key)}
                                    disabled={saving}
                                  >
                                    {saving ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <>
                                        <i className="ri-save-line me-1"></i>
                                        {t("termsCondition.buttons.save")}
                                      </>
                                    )}
                                  </Button>
                                </div>

                                {activeSubTab === item.key && (
                                  <CommonRichTextEditor
                                    editorKey={`${item.key}_v${dataVersion}`}
                                    value={termsData[item.key] || ""}
                                    onChange={(data) => handleEditorChange(item.key, data)}
                                    placeholder={t("termsCondition.editorPlaceholder")}
                                    minHeight={300}
                                  />
                                )}

                                {termsData[item.key] && (
                                  <div className="mt-3">
                                    <small className="text-muted">
                                      <i className="ri-information-line me-1"></i>
                                      {t("termsCondition.contentLength", {
                                        length: termsData[item.key].replace(/<[^>]*>/g, "").length,
                                      })}
                                    </small>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </TabPane>
                        ))}
                      </TabContent>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default withTranslation()(TermsCondition);
